import { useSelector } from 'react-redux';
import { useAlert } from '@hooks/useAlert';
import type { RootState } from '@features/redux/store';
import { fetchOrgData, OrgUrls } from '@api/org';
import { useState } from 'react';
import { json2csv } from 'json-2-csv';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { Button } from '@components/design-system';
import type { EmployeeSearchResponse } from '@/types/newEmployees';

interface ExportEmployeesProps {
    params: Record<string, string | undefined>;
}

/**
 * Calls the unpaginated management/employees/search endpoint to fetch
 * every employee matching the current org/search filters, then writes
 * the result to a CSV.
 */
export const ExportEmployees = ({ params }: ExportEmployeesProps) => {
    const { IdToken } = useSelector((state: RootState) => state.auth);

    const [loading, setLoading] = useState(false);

    const { setAlert } = useAlert();

    const downloadCSV = async () => {
        setLoading(true);

        const data = await fetchOrgData<EmployeeSearchResponse>(IdToken!, OrgUrls.SearchEmployees, params).catch(
            (err) => setAlert(err.message, 'error')
        );

        setLoading(false);

        if (!data) {
            setAlert('Failed to export data', 'error');
            return;
        }

        const csv = await json2csv(data.employees, {
            excludeKeys: [
                'EmployeeID',
                'ResellerID',
                'ParentCompanyID',
                'CompanyName',
                'Version',
                'RowNum',
                'TotalRecords',
            ],
        });

        const csvData = `data:text/csv;base64,${btoa(unescape(encodeURIComponent(csv)))}`;

        const link = document.createElement('a');
        link.setAttribute('href', csvData);
        link.setAttribute('download', 'employees-table.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <Button
            variant='secondary'
            size='small'
            startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />}
            onClick={downloadCSV}
            loading={loading}>
            Export
        </Button>
    );
};
